'use client';

import {
  createContext,
  useCallback,
  useEffect,
  useState,
} from 'react';
import {
  api,
  clearTokens,
  setAccessToken,
  restoreSession,
} from '@/lib/api';
import { getSafeRedirectPath } from '@/lib/auth-redirect';
import type { AuthUser, UserPreferences } from '@/lib/types';

export type { AuthUser, UserPreferences, Company } from '@/lib/types';

export interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (
    credentials: { email?: string; phone?: string; password: string },
    rememberMe?: boolean
  ) => Promise<{ success: boolean; error?: string }>;
  register: (data: {
    email: string;
    password: string;
    password_confirmation: string;
  }) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  updatePreferences: (prefs: Partial<UserPreferences>) => Promise<void>;
}

// ── Контекст ──────────────────────────────────────────────────────────────────

export const AuthContext = createContext<AuthContextValue | null>(null);

// ── Provider ──────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Загрузить текущего пользователя из API.
   */
  const refreshUser = useCallback(async () => {
    try {
      const response = await api.get<{ user: AuthUser }>('/api/v1/me');
      if (response.data?.user) {
        setUser(response.data.user);
      }
    } catch {
      setUser(null);
      clearTokens();
    }
  }, []);

  /**
   * Инициализация: access в памяти → иначе refresh по HttpOnly cookie.
   */
  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      const restored = await restoreSession();
      if (restored) {
        await refreshUser();
      }
      setIsLoading(false);
    };

    void init();
  }, [refreshUser]);

  /**
   * Подписываемся на события API-клиента (401/403).
   */
  useEffect(() => {
    const handleUnauthorized = () => {
      setUser(null);
      clearTokens();
      const currentPath = window.location.pathname + window.location.search;
      const redirect = getSafeRedirectPath(currentPath);
      const params = new URLSearchParams();
      if (redirect !== '/') {
        params.set('redirect', redirect);
      }
      window.location.href = params.toString() ? `/login?${params.toString()}` : '/login';
    };

    window.addEventListener('api:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('api:unauthorized', handleUnauthorized);
  }, []);

  /**
   * Вход в систему.
   */
  const login = useCallback(async (
    credentials: { email?: string; phone?: string; password: string },
    rememberMe = true
  ) => {
    try {
      const response = await api.post<{
        access_token: string;
        user: AuthUser;
      }>('/api/v1/auth/login', {
        ...credentials,
        remember_me: rememberMe,
      });

      if (response.data && response.data.access_token) {
        setAccessToken(response.data.access_token, rememberMe);
        setUser(response.data.user);
        return { success: true };
      }

      const errorMsg = response.errors
        ? Object.values(response.errors).flat().join(', ')
        : (response.message || 'Ошибка входа');
      return { success: false, error: errorMsg };

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ошибка входа';
      return { success: false, error: message };
    }
  }, []);

  /**
   * Регистрация нового тенанта.
   */
  const register = useCallback(async (data: {
    email: string;
    password: string;
    password_confirmation: string;
  }) => {
    try {
      const response = await api.post<{
        access_token: string;
        user: AuthUser;
      }>('/api/v1/auth/register', {
        ...data,
        registration_source: 'app',
      });

      if (response.data && response.data.access_token) {
        setAccessToken(response.data.access_token, true);
        setUser(response.data.user);
        return { success: true };
      }

      const errorMsg = response.errors
        ? Object.values(response.errors).flat().join(', ')
        : (response.message || 'Ошибка регистрации');
      return { success: false, error: errorMsg };

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ошибка регистрации';
      return { success: false, error: message };
    }
  }, []);

  /**
   * Выход из системы.
   */
  const logout = useCallback(async () => {
    try {
      await api.post('/api/v1/auth/logout', {});
    } catch {
      // Логаут должен работать даже при ошибке API
    } finally {
      setUser(null);
      clearTokens();
      window.location.href = '/login';
    }
  }, []);

  /**
   * Обновить настройки пользователя.
   */
  const updatePreferences = useCallback(async (prefs: Partial<UserPreferences>) => {
    try {
      await api.patch('/api/v1/preferences', prefs);
      setUser(prev => prev ? {
        ...prev,
        preferences: { ...(prev.preferences ?? {} as UserPreferences), ...prefs },
      } : null);
    } catch (err) {
      console.error('Ошибка сохранения настроек:', err);
    }
  }, []);

  const value: AuthContextValue = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    refreshUser,
    updatePreferences,
  };

  return (
    // React 19: <Context> вместо <Context.Provider>
    <AuthContext value={value}>
      {children}
    </AuthContext>
  );
}
